import { InjectionToken } from '@angular/core';

/**
 * Imports a module package's entry point. The returned namespace object is
 * expected to carry the module definition under `SAPHIR_MODULE_EXPORT`.
 */
export type ModulePackageImport = () => Promise<unknown>;

export type ModulePackageMap = Readonly<Record<string, ModulePackageImport>>;

/**
 * The npm delivery channel: every module package installed in this shell,
 * keyed by module id.
 *
 * This is the one place that has to change when a module is added — the import
 * specifier must be a literal, or the bundler cannot resolve it, which is why
 * it cannot come from `modules.config.json`. Whether an installed module is
 * actually offered *is* runtime configuration and lives there instead.
 *
 * Each `import()` pulls in the package entry point only: the manifest and a
 * couple of closures. The module's screens sit behind its `loadRoutes()`, in a
 * chunk that is fetched when the module is opened.
 *
 * ```ts
 * export const MODULE_PACKAGE_IMPORTS: ModulePackageMap = {
 *   lohn: () => import('@saphir/lohn-module'),
 * };
 * ```
 */
export const MODULE_PACKAGE_IMPORTS: ModulePackageMap = {
  lohn: () => import('@saphir/lohn-module'),
  // Installed from a local tarball for now (`npm run sync:module -- <FaktFrontend>`);
  // the feed publication comes with the module's own pipeline.
  fakt: () => import('@saphir/fakt-module'),
  fleet: () => import('@saphir/fleet-module'),
  calendar: () => import('@saphir/calendar-module'),
};

/**
 * Indirection over the map above so specs can register fake packages instead of
 * importing real ones.
 */
export const MODULE_PACKAGES = new InjectionToken<ModulePackageMap>('MODULE_PACKAGES', {
  providedIn: 'root',
  factory: () => MODULE_PACKAGE_IMPORTS,
});
